import { useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { ACCENT_COLOR } from "../theme";

export default function WizardInput({ label, icon, value, onChange, onKeyDown, onBlur, onFocus, placeholder, type = "text", autoFocus, multiline, inputRef: externalRef }) {
  const inputRef = useRef(null);
  const setRef = (el) => {
    inputRef.current = el;
    if (externalRef) externalRef.current = el;
  };

  const resize = useCallback(() => {
    const el = inputRef.current;
    if (!el || !multiline) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  }, [multiline]);

  useEffect(() => {
    resize();
  }, [value, resize]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const el = inputRef.current;
      el.focus();
      if (multiline) {
        el.setSelectionRange(el.value.length, el.value.length);
      } else {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    }
  }, [autoFocus, multiline]);

  const InputTag = multiline ? Textarea : Input;

  return (
    <Field>
      {label && (
        <Label>
          {icon && <i className={icon.startsWith("fa") && !icon.startsWith("fa-") ? icon : `fas ${icon}`} />} {label}
        </Label>
      )}
      <InputTag
        ref={setRef}
        type={multiline ? undefined : type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onFocus={onFocus}
        placeholder={placeholder}
        rows={multiline ? 1 : undefined}
      />
    </Field>
  );
}

const Label = styled.label`
  font-size: 16px;
  color: #4b5563;
  padding-left: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.15s;

  i {
    font-size: 13px;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  &:focus-within ${Label} {
    color: ${ACCENT_COLOR};
  }
`;

const inputStyles = `
  padding: 14px 16px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 22px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    box-shadow: 0 0 4px 2px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    box-shadow: 0 0 4px 2px ${ACCENT_COLOR};
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Input = styled.input`
  ${inputStyles}

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const Textarea = styled.textarea`
  ${inputStyles}
  resize: none;
  overflow: hidden;
  line-height: 1.4;
`;
